<template>
  <main class="min-h-screen w-screen flex justify-center items-center transition-all lg:h-screen">
    <Toast position="top-right" />
    <ConfirmDialog />
    <LogoutConfirmDialog />
    <PauseCreateConfirmDialog />
    <GlobalConnectivityAlert v-if="showGlobalConnectivityAlert" />
    <TabSessionBlockedOverlay />
    <!-- <ConnectivityPictureInPicture /> -->
    <!-- La pestana bloqueada no se tapa: se apaga. Con el layout montado
         debajo seguian vivos el webphone (que re-registra la extension SIP y
         le roba el contacto a la pestana que trabaja), el socket de monitor y
         las comprobaciones de sesion, y cualquiera de ellas podia dar la
         sesion por invalida y limpiar el localStorage, que es del navegador y
         no de la pestana. -->
    <component :is="layout" v-if="!isTabSessionBlocked">
      <router-view />
    </component>
  </main>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import ConfirmDialog from 'primevue/confirmdialog'
import Toast from 'primevue/toast'

import LogoutConfirmDialog from '@/components/atoms/sidebars/components/LogoutConfirmDialog.vue'
import GlobalConnectivityAlert from '@/components/molecules/connectivity/GlobalConnectivityAlert.vue'
// import ConnectivityPictureInPicture from '@/components/molecules/connectivity/ConnectivityPictureInPicture.vue'
import PauseCreateConfirmDialog from '@/components/molecules/pause/PauseCreateConfirmDialog.vue'
import TabSessionBlockedOverlay from '@/components/molecules/session/TabSessionBlockedOverlay.vue'
import { useSeoMeta } from '@unhead/vue'
import { useI18n } from 'vue-i18n'

import AuthLayout from '@/layouts/AuthLayout.vue'
import DashboardLayout from '@/layouts/DashboardLayout.vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { useI18nStore } from '@/composables/useI18nStore'
import { useWhatsAppBackground } from '@/composables/useWhatsAppBackground'
import { useAuthStore } from '@/stores/useAuthStore'
import { useConnectivityStore } from '@/stores/useConnectivityStore'
import { usePauseStatusStore } from '@/stores/usePauseStatusStore'
import { useTabSessionStore } from '@/stores/useTabSessionStore'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'

export default defineComponent({
  components: {
    DefaultLayout,
    AuthLayout,
    DashboardLayout,
    Toast,
    ConfirmDialog,
    LogoutConfirmDialog,
    PauseCreateConfirmDialog,
    GlobalConnectivityAlert,
    TabSessionBlockedOverlay,
    // ConnectivityPictureInPicture,
  },
  setup() {
    const route = useRoute()
    const i18n = useI18n()
    const i18nStore = useI18nStore()
    const authStore = useAuthStore()
    const connectivityStore = useConnectivityStore()
    const pauseStatusStore = usePauseStatusStore()
    const workspace = useWorkspaceStore()
    const tabSession = useTabSessionStore()
    const { initializeBackground, disconnectBackground } = useWhatsAppBackground()

    const layout = computed(() => {
      const layoutName = (route.meta.layout as string) || 'DefaultLayout'
      return layoutName
    })
    const showGlobalConnectivityAlert = computed(() => route.name !== 'sign-in')
    const isTabSessionBlocked = computed(() => tabSession.status === 'blocked')

    const pageTitle = computed<string>(() => {
      const titleKey = route.meta.title as string
      const title = titleKey ? i18n.t(`${titleKey}`) : i18n.t('common.titles.home')
      const callStatusTitles = {
        calling: i18n.t('contact_info.calling'),
        incoming: i18n.t('contact_info.incoming_call'),
        connected: i18n.t('contact_info.in_call'),
        held: i18n.t('contact_info.on_hold'),
      } as const

      if (workspace.webphoneCallStatus !== 'idle') {
        return callStatusTitles[workspace.webphoneCallStatus]
      }

      return pauseStatusStore.isPaused ? i18n.t('call.on_break') : title
    })

    useSeoMeta({
      title: pageTitle,
      description: 'CallOperator - Sistema de gestión de llamadas y comunicaciones',
      titleTemplate: `%s | ${i18n.t('common.titles.app_name')}`,
      ogTitle: pageTitle,
      ogDescription: 'CallOperator - Sistema de gestión de llamadas y comunicaciones',
      ogImage: '/og-image.jpg',
      ogUrl: () => window.location.href,
      twitterCard: 'summary_large_image',
    })

    watch(
      () => i18nStore.language,
      (newLang) => {
        i18n.locale.value = newLang
      },
      { immediate: true },
    )

    onMounted(async () => {
      connectivityStore.installNetworkListeners()
      await authStore.initializeAuth()
    })

    watch(
      () => [authStore.isAuthenticated, authStore.userId] as const,
      async ([isAuthenticated, userId]) => {
        if (isAuthenticated && userId) {
          // No se conecta nada hasta saber si esta pestaña es la que trabaja:
          // el backend guarda un websocket por usuario, así que una segunda
          // pestaña desplazaría a la primera. `claim` deja el estado en
          // 'active' o 'blocked' y el watcher de abajo actúa en consecuencia.
          await tabSession.claim(userId)
          return
        }

        tabSession.release()
        disconnectBackground()
      },
      { immediate: true },
    )

    // Una sola vía para conectar y desconectar: el estado de la pestaña. Cubre
    // tanto el arranque como el relevo ("Usar aquí" en una pestaña deja a la
    // otra en 'blocked', y esta la desconecta).
    watch(
      () => tabSession.status,
      (status) => {
        if (status === 'active') {
          void initializeBackground()
          return
        }

        disconnectBackground()
      },
    )

    onUnmounted(() => {
      connectivityStore.removeNetworkListeners()
      tabSession.release()
      disconnectBackground()
    })

    return { layout, showGlobalConnectivityAlert, isTabSessionBlocked }
  },
})
</script>
